import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
  Container,
  Fade,
  useTheme,
} from "@mui/material";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useNavigate } from "react-router-dom";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth();
const monthYearLabel = new Date(currentYear, currentMonth, 1).toLocaleString(undefined, {
  month: "long",
  year: "numeric",
});

const dateInCurrentMonth = (day: number) => new Date(currentYear, currentMonth, day);

const dummyExpenses = [
  { id: "1", description: "Coffee at Starbucks", amount: 4.5, date: dateInCurrentMonth(20) },
  { id: "2", description: "Groceries", amount: 45.0, date: dateInCurrentMonth(18) },
  { id: "3", description: "Movie ticket", amount: 12.0, date: dateInCurrentMonth(15) },
];

const dummyIncomes = [
  { id: "1", description: "Freelance Project", amount: 500, date: dateInCurrentMonth(21) },
  { id: "2", description: "Salary", amount: 3000, date: dateInCurrentMonth(1) },
  { id: "3", description: "Gift", amount: 100, date: dateInCurrentMonth(10) },
];

const formatCurrency = (amount: number) => `$${amount.toFixed(2)}`;

const GuestHomePageComponent = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const totalExpenses = dummyExpenses.reduce((sum, e) => sum + e.amount, 0);
  const totalIncome = dummyIncomes.reduce((sum, i) => sum + i.amount, 0);
  const netBalance = totalIncome - totalExpenses;

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Fade in={true} timeout={1000}>
        <Box>
          {/* Hero Section */}
          <Box 
            sx={{ 
              textAlign: "center", 
              mb: 8,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.secondary.main}10 100%)`,
              borderRadius: 4,
              p: 6,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 30% 40%, rgba(37, 99, 235, 0.1) 0%, transparent 50%)',
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h1"
                gutterBottom
                sx={{ 
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Welcome to PocketBalance
              </Typography>

              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ mb: 4, maxWidth: 600, mx: "auto", fontWeight: 400 }}
              >
                Take control of your finances with smart tracking, insightful reports, 
                and beautiful visualizations — all in one modern platform.
              </Typography>

              <Button
                variant="contained"
                size="large"
                startIcon={<AccountBalanceWalletIcon />}
                sx={{ 
                  px: 4, 
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 25px ${theme.palette.primary.main}40`,
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
                onClick={() => navigate(LOGIN_ROUTE_PATH)}
              >
                Start Tracking Now
              </Button>
            </Box>
          </Box>

          <Divider sx={{ mb: 6 }} />

          {/* Demo Overview Header */}
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}
            >
              <TrendingUpIcon color="primary" sx={{ fontSize: '2rem' }} />
              {`Demo Overview — ${monthYearLabel}`}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500, mx: 'auto' }}>
              Here's how your financial dashboard could look with real data
            </Typography>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.error.light}20 0%, ${theme.palette.error.main}20 100%)`,
                  border: `1px solid ${theme.palette.error.light}40`,
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <MoneyOffIcon sx={{ fontSize: 48, color: theme.palette.error.main }} />
                  </Box>
                  <Typography variant="h6" color="error.main" gutterBottom sx={{ fontWeight: 600 }}>
                    Total Expenses
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="error.main">
                    {formatCurrency(totalExpenses)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.success.light}20 0%, ${theme.palette.success.main}20 100%)`,
                  border: `1px solid ${theme.palette.success.light}40`,
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <AttachMoneyIcon sx={{ fontSize: 48, color: theme.palette.success.main }} />
                  </Box>
                  <Typography variant="h6" color="success.main" gutterBottom sx={{ fontWeight: 600 }}>
                    Total Income
                  </Typography>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {formatCurrency(totalIncome)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={12} md={4}>
              <Card 
                sx={{ 
                  background: `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.primary.main}20 100%)`,
                  border: `1px solid ${theme.palette.primary.light}40`,
                  height: '100%',
                }}
              >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
                  </Box>
                  <Typography variant="h6" color="primary.main" gutterBottom sx={{ fontWeight: 600 }}>
                    Net Balance
                  </Typography>
                  <Typography 
                    variant="h3" 
                    fontWeight="bold" 
                    color={netBalance >= 0 ? "success.main" : "error.main"}
                  >
                    {netBalance >= 0 ? '+' : ''}{formatCurrency(netBalance)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Transaction Lists */}
          <Grid container spacing={4}>
            {/* Recent Expenses */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 600 }}
                    >
                      <MoneyOffIcon color="error" />
                      Recent Expenses
                    </Typography>
                  </Box>
                  
                  <List sx={{ pt: 0 }}>
                    {dummyExpenses.map(({ id, description, amount, date }, index) => (
                      <Box key={id}>
                        <ListItem
                          sx={{
                            px: 3,
                            py: 2,
                            '&:hover': { 
                              bgcolor: theme.palette.error.light + '10',
                              transform: 'translateX(4px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            cursor: 'pointer',
                          }}
                        >
                          <ListItemText
                            primary={description}
                            secondary={date.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                            primaryTypographyProps={{ 
                              fontWeight: 500,
                              color: 'text.primary'
                            }}
                            secondaryTypographyProps={{ 
                              fontSize: '0.875rem', 
                              color: 'text.secondary' 
                            }}
                          />
                          <Typography 
                            variant="h6" 
                            color="error.main" 
                            fontWeight="bold"
                            sx={{ minWidth: 'fit-content' }}
                          >
                            -{formatCurrency(amount)}
                          </Typography>
                        </ListItem>
                        {index < dummyExpenses.length - 1 && <Divider sx={{ mx: 3 }} />}
                      </Box>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>

            {/* Recent Incomes */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ p: 3, pb: 2 }}>
                    <Typography
                      variant="h5"
                      sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 600 }}
                    >
                      <AttachMoneyIcon color="success" />
                      Recent Incomes
                    </Typography>
                  </Box>
                  
                  <List sx={{ pt: 0 }}>
                    {dummyIncomes.map(({ id, description, amount, date }, index) => (
                      <Box key={id}>
                        <ListItem
                          sx={{
                            px: 3,
                            py: 2,
                            '&:hover': { 
                              bgcolor: theme.palette.success.light + '10',
                              transform: 'translateX(4px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                            cursor: 'pointer',
                          }}
                        >
                          <ListItemText
                            primary={description}
                            secondary={date.toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                            primaryTypographyProps={{ 
                              fontWeight: 500,
                              color: 'text.primary'
                            }}
                            secondaryTypographyProps={{ 
                              fontSize: '0.875rem', 
                              color: 'text.secondary' 
                            }}
                          />
                          <Typography 
                            variant="h6" 
                            color="success.main" 
                            fontWeight="bold"
                            sx={{ minWidth: 'fit-content' }}
                          >
                            +{formatCurrency(amount)}
                          </Typography>
                        </ListItem>
                        {index < dummyIncomes.length - 1 && <Divider sx={{ mx: 3 }} />}
                      </Box>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Call to Action */}
          <Box 
            sx={{ 
              mt: 8, 
              textAlign: 'center',
              p: 4,
              background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${theme.palette.grey[100]} 100%)`,
              borderRadius: 3,
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Typography
              variant="h5"
              color="text.primary"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Ready to take control of your finances?
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 400, mx: 'auto' }}
            >
              Sign in to save your own data and get personalized insights that help you make better financial decisions.
            </Typography>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
};

export default GuestHomePageComponent;
